/**
 * Código de barras do ingresso — 13 dígitos com verificador EAN-13.
 *
 * Porte fiel de `Ingresso.checkSum` / `Ingresso.isValidCodbar` do TMobi original
 * (app/src/main/java/br/com/tmobi/models/Ingresso.java:534).
 */

export const TAMANHO_CODBAR = 13;

/** Dígito verificador EAN-13 calculado sobre os 12 primeiros dígitos (pesos 1 e 3). */
export function digitoVerificador(dozeDigitos: string): number {
  let soma = 0;
  for (let i = 0; i < dozeDigitos.length; i++) {
    const digito = Number(dozeDigitos[i]);
    soma += (Number.isNaN(digito) ? 0 : digito) * (i % 2 === 0 ? 1 : 3);
  }
  const verificador = 10 - (soma % 10);
  return verificador === 10 ? 0 : verificador;
}

/**
 * Completa com zeros à esquerda até 13 dígitos — leitor e digitação costumam
 * comer o zero inicial. O original fazia isso num laço; aqui é direto.
 */
export function normalizarCodbar(codbar: string): string {
  const limpo = codbar.trim();
  return limpo.length >= TAMANHO_CODBAR
    ? limpo
    : limpo.padStart(TAMANHO_CODBAR, '0');
}

/** Só é código de ingresso se tiver 13 dígitos e o verificador bater. */
export function codbarValido(codbar: string): boolean {
  if (!/^\d{13}$/.test(codbar)) {
    return false;
  }
  return digitoVerificador(codbar.slice(0, 12)) === Number(codbar[12]);
}

/**
 * Números de lote que o código pode indicar, do mais específico para o herdado:
 *
 * 1. os 5 primeiros dígitos — é o formato atual (`38266` → lote "38266 - 38266");
 * 2. os dígitos 2 a 5, que é o que o TMobi original casava
 *    (`codbar.substring(1, 5)`), e que ainda pega lote antigo de 4 dígitos.
 *
 * A ordem importa: casar por 4 dígitos com LIKE pode acertar o lote errado
 * (`8266` casa tanto com `38266` quanto com um `48266`), então o de 5 vem antes.
 */
export function candidatosDeLote(codbar: string): string[] {
  return [codbar.slice(0, 5), codbar.slice(1, 5)];
}

/**
 * Puxa o código de ingresso de dentro do que o leitor devolveu.
 *
 * O QR do ingresso raramente vem limpo: costuma ser uma URL de validação, ou
 * o código com prefixo/quebra de linha. Sem esta limpeza o app recusaria um
 * ingresso bom dizendo "Ingresso invalido".
 */
export function extrairCodbar(bruto: string): string {
  const texto = String(bruto ?? '').trim();

  if (texto.length === 0) {
    return '';
  }

  // Veio limpo (leitura de EAN-13, digitação): caminho mais comum.
  if (codbarValido(texto)) {
    return texto;
  }

  const sequencias = texto.match(/\d+/g) ?? [];

  // Melhor sinal possível: uma janela de 13 dígitos cujo verificador fecha.
  for (const sequencia of sequencias) {
    for (let i = 0; i + TAMANHO_CODBAR <= sequencia.length; i++) {
      const janela = sequencia.slice(i, i + TAMANHO_CODBAR);
      if (codbarValido(janela)) {
        return janela;
      }
    }
  }

  // Nenhuma fecha: fica a maior sequência de dígitos, completada com zeros.
  // Se ainda assim não prestar, a regra recusa e mostra o que foi lido.
  const maior = sequencias.reduce((a, b) => (b.length > a.length ? b : a), '');

  return maior.length > 0
    ? normalizarCodbar(maior.slice(0, TAMANHO_CODBAR))
    : normalizarCodbar(texto);
}

/**
 * Janela de validade de um passaporte: o intervalo em que aquele ingresso vale.
 *
 * Vem do QR, não do banco — quem vendeu gravou ali a ativação e o vencimento do
 * lote de passaporte.
 */
export interface JanelaValidade {
  inicio: Date;
  fim: Date;
}

/**
 * O que saiu de uma leitura: o código e, quando o QR traz, a janela de datas.
 *
 * `janela`:
 *   - `null` — leitura sem restrição de data. É o caso do código nu e do
 *     ingresso comum, que o TMobi grava com `dataIni` vazia;
 *   - `'ilegivel'` — o QR declarou ter janela mas as datas não deram para ler.
 *     Recusar é mais seguro do que fingir que não havia restrição.
 */
export interface LeituraIngresso {
  codbar: string;
  janela: JanelaValidade | 'ilegivel' | null;
}

/** `yyyy-MM-dd HH:mm:ss`, o formato que o TMobi grava no QR. */
const FORMATO_DATA = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

/**
 * Data do QR no fuso do aparelho.
 *
 * O original usava `SimpleDateFormat` com o `Locale` padrão e sem fuso, o que
 * dá exatamente isto: a data é lida como hora local de quem está lendo. Manter
 * assim é o que faz a janela significar a mesma coisa nos dois apps.
 *
 * Devolve `null` quando o texto não é uma data — inclusive em caso de dia que
 * não existe, que o `Date` do JavaScript aceitaria empurrando para o mês
 * seguinte.
 */
export function analisarDataDoQr(texto: string): Date | null {
  const achado = FORMATO_DATA.exec(String(texto ?? '').trim());
  if (!achado) {
    return null;
  }

  const ano = Number(achado[1]);
  const mes = Number(achado[2]);
  const dia = Number(achado[3]);
  const hora = Number(achado[4]);
  const minuto = Number(achado[5]);
  const segundo = Number(achado[6]);

  const data = new Date(ano, mes - 1, dia, hora, minuto, segundo, 0);

  const bate =
    data.getFullYear() === ano &&
    data.getMonth() === mes - 1 &&
    data.getDate() === dia &&
    data.getHours() === hora &&
    data.getMinutes() === minuto &&
    data.getSeconds() === segundo;

  return bate ? data : null;
}

/**
 * O JSON que o TMobi grava no ingresso, quando é isso que veio.
 *
 * Formato de `FinalizarActivity` (app original): `eventoId`, `loteId`,
 * `codebar`, `dataIni`, `dataFinal`, `idPassLote`. Repare no `codebar` com
 * "e" — é assim no original, e mudar aqui quebraria a leitura de ingresso já
 * impresso. Aceitamos `codbar` também, que é como o resto do domínio escreve.
 *
 * Texto que não parseia não vira passaporte: segue como leitura comum, e o
 * dígito verificador continua sendo quem decide se aquilo é um código.
 */
function comoIngressoDoTMobi(texto: string): Record<string, unknown> | null {
  if (!texto.startsWith('{')) {
    return null;
  }
  try {
    const dados: unknown = JSON.parse(texto);
    if (!dados || typeof dados !== 'object') {
      return null;
    }
    const objeto = dados as Record<string, unknown>;
    return 'codebar' in objeto || 'codbar' in objeto ? objeto : null;
  } catch {
    return null;
  }
}

/**
 * Interpreta o que o leitor devolveu: código e janela de validade.
 *
 * Porte de `CValidaActivity.handleResult` do TMobi original, que decidia pelo
 * tamanho do texto — até 13 caracteres era código nu, acima disso era o JSON do
 * ingresso. Aqui a decisão é pelo conteúdo e não pelo tamanho: uma URL de
 * validação também passa dos 13 e não é passaporte nenhum.
 *
 * O teste de "tem janela?" é o mesmo do original (`dataIni.length() > 5`), que
 * é como ele separa o passaporte do ingresso comum — os dois viajam no mesmo
 * JSON, e no comum as datas vão vazias.
 */
export function interpretarLeitura(bruto: string): LeituraIngresso {
  const texto = String(bruto ?? '').trim();
  const ingresso = comoIngressoDoTMobi(texto);

  if (!ingresso) {
    return {codbar: extrairCodbar(texto), janela: null};
  }

  const codbar = extrairCodbar(String(ingresso.codebar ?? ingresso.codbar ?? ''));
  const dataIni = String(ingresso.dataIni ?? '').trim();

  if (dataIni.length <= 5) {
    return {codbar, janela: null};
  }

  const inicio = analisarDataDoQr(dataIni);
  const fim = analisarDataDoQr(String(ingresso.dataFinal ?? ''));

  if (!inicio || !fim) {
    return {codbar, janela: 'ilegivel'};
  }

  return {codbar, janela: {inicio, fim}};
}

